import React, { useEffect, useState, useRef } from "react";
import ApiService from "../../../Services/user/service";
import { Heart, TrendingUp, HeartIcon } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import TradingViewChart from "../../../components/tradechart";
import "./Dashboard.css";
import { toast } from "react-toastify";
import { Wallet, ArrowDownToLine } from "lucide-react";
import { useLivePrices } from "../../../context/LivePriceContext";
import socket from "../../../Utils/socketClient";


const Dashboard = () => {
  const { livePrices } = useLivePrices();
  const [indexSymbols, setIndexSymbols] = useState([]);
  const sliderRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [depositModal, setDepositModal] = useState(false);
  const [depositAmount, setDepositAmount] = useState("");
  const [transactionId, setTransactionId] = useState("");
  const [receiptBase64, setReceiptBase64] = useState("");
  const [typeToFetch, setTypeToFetch] = useState("volume");
  const [marketAnalysis, setMarketAnalysis] = useState([]);
  const currency = localStorage.getItem("currentCurrency");
  const username = localStorage.getItem("UserName") || "User";
  const userid = localStorage.getItem("user_id") || "User";
  const [paymentMethod, setPaymentMethod] = useState("");
  const depositQuickAmounts =
    currency === "1"
      ? [10000, 25000, 50000]   // INR
      : [100, 500, 1000];     // USD
  const navigate = useNavigate();

  const location = useLocation();
  const [dashboardPositions, setDashboardPositions] = useState([]);
  const [dashboardHoldings, setDashboardHoldings] = useState([]);
  const [posTab, setPosTab] = useState("positions");
  const searchParams = new URLSearchParams(location.search);
  const querySymbol = searchParams.get("symbol");
  const queryToken = searchParams.get("token");
  const [selectedSymbol, setSelectedSymbol] = useState(querySymbol || location.state?.symbol || (currency === "1" ? "NIFTY" : "AUDCAD"));
  const [selectedToken, setSelectedToken] = useState(queryToken || location.state?.token || "");

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const qSym = params.get("symbol");
    const qTok = params.get("token");
    if (qSym) {
      setSelectedSymbol(qSym);
      setSelectedToken(qTok || "");
    } else if (location.state && location.state.symbol) {
      setSelectedSymbol(location.state.symbol);
      setSelectedToken(location.state.token || "");
    }
  }, [location.search, location.state]);

  const fetchDashboardPositions = async () => {
    try {
      let res;
      if (currency === "1") {
        res = await ApiService.PositionInr({ userid: userid });
      } else {
        res = await ApiService.PositionUsd({ userid: userid });
      }
      setDashboardPositions(res?.data?.data || []);
    } catch (error) {
      console.error("Dashboard positions error", error);
    }
  };

  const fetchDashboardHoldings = async () => {
    try {
      let res;
      if (currency === "1") {
        res = await ApiService.HoldingInr({ userid: userid });
      } else {
        res = await ApiService.HoldingUsd({ userid: userid });
      }
      setDashboardHoldings(res?.data?.data || []);
    } catch (error) {
      console.error("Dashboard holdings error", error);
    }
  };

  useEffect(() => {
    fetchDashboardPositions();
    fetchDashboardHoldings();
  }, [currency]);

  const calculatePNL = (p) => {
    if (currency !== "1" && p.PL !== undefined && p.total_amt !== undefined) {
      return Number(p.PL) || 0;
    }
    const buyPrice = Number(p.buy_price || 0);
    const livePrice = Number(livePrices[p.token] || p.liveprice || 0);
    const qty = Number(p.buy_qty || 0);

    if (buyPrice > 0 && livePrice > 0 && qty !== 0) {
      if (p.Mk_type === "forex") {
        const invested = buyPrice * Math.abs(qty);
        const current = (livePrice / buyPrice) * invested;
        return (current - invested) * (qty < 0 ? -1 : 1);
      } else {
        return (livePrice - buyPrice) * qty;
      }
    }
    return 0;
  };

  const [funds, setFunds] = useState([]);
  const banners = [
    {
      title: "📊 Advanced Analytics",
      desc: "Track your trades in real time",
      btn: "Try Now",
      class: "banner-1",
      class: "blue",
    },
    {
      title: "🔔 Custom Alerts",
      desc: "Never miss important market moves",
      btn: "Enable Alerts",
      class: "banner-2",
      class: "green",
    },
    {
      title: "⚡ Smart Trading",
      desc: "Trade faster with smart insights",
      btn: "Explore",
      class: "banner-3",
      class: "orange",
    },
  ];
  const tabs = ["volume", "price", "gainers", "favorites", "losers"];

  const tabsUsd = ["Top", "Favorites", "Popular"];
  const [Faoriteusddata, setFavoriteusddata] = useState([]);
  const [userdetails, setUserdetails] = useState([]);

  const USD_TO_INR = 87;
  const convertedInr =
    currency !== "1" && depositAmount
      ? (Number(depositAmount) * USD_TO_INR).toFixed(2)
      : "";
  const getChangeClass = (change) => {
    if (!change) return "text-muted";
    return change.trim().startsWith("-") ? "text-danger" : "text-success";
  };

  const getUserDetail = async (user_id) => {
    const res = await ApiService.getUserDetail(user_id);
    setFunds(res?.data?.data || []);

  };

  useEffect(() => {
    getUserDetail(userid);
  }, []);

  const fetchIndexSymbols = async () => {
    try {
      const res = await ApiService.indexSymbols();
      if (res.data && res.data.status) {
        setIndexSymbols(res.data.data);
      }
    } catch (err) {
      console.error("Error fetching index symbols:", err);
    }
  };

  useEffect(() => {
    if (currency === "1") {
      fetchIndexSymbols();
    }
  }, [currency]);

  useEffect(() => {
    let subscribedChannels = null;
    if (currency === "1" && indexSymbols.length > 0) {
      const subscribe = () => {
        const channels = indexSymbols
          .filter(item => item.exch_seg && item.instrument_token)
          .map(item => `${item.exch_seg}|${item.instrument_token}`)
          .join("#");
        if (channels) {
          socket.emit("subscribe", channels);
          subscribedChannels = channels;
        }
      };

      if (socket.connected) {
        subscribe();
      }
      socket.on("connect", subscribe);
      return () => {
        socket.off("connect", subscribe);
        if (subscribedChannels) {
          socket.emit("unsubscribe", subscribedChannels);
        }
      };
    }
  }, [indexSymbols, currency]);

  useEffect(() => {
    let subscribedChannels = null;
    if (currency === "1" && marketAnalysis.length > 0) {
      const subscribeMarketAnalysis = () => {
        const channels = marketAnalysis
          .filter(item => item.exch_seg && item.instrument_token)
          .map(item => `${item.exch_seg}|${item.instrument_token}`)
          .join("#");
        if (channels) {
          socket.emit("subscribe", channels);
          subscribedChannels = channels;
        }
      };

      if (socket.connected) {
        subscribeMarketAnalysis();
      }
      socket.on("connect", subscribeMarketAnalysis);
      return () => {
        socket.off("connect", subscribeMarketAnalysis);
        if (subscribedChannels) {
          socket.emit("unsubscribe", subscribedChannels);
        }
      };
    }
  }, [marketAnalysis, currency]);

  useEffect(() => {
  }, [indexSymbols]);

  useEffect(() => {
    const handleTick = (tick) => {
    };
    socket.on("alice_ticks", handleTick);
    return () => {
      socket.off("alice_ticks", handleTick);
    };
  }, []);

  const getIndexLivePrice = (symbolName, fallbackVal) => {
    const symbolObj = indexSymbols.find(
      (item) => item.symbol?.toUpperCase() === symbolName.toUpperCase()
    );
    if (!symbolObj) return fallbackVal;
    const token = symbolObj.instrument_token;
    const price = livePrices[token];
    return price || fallbackVal;
  };

  const markets = [
    { title: "NIFTY 50", symbol: "NIFTY", value: getIndexLivePrice("NIFTY", "26142.70"), change: "+0.42%" },
    { title: "BANK NIFTY", symbol: "BANKNIFTY", value: getIndexLivePrice("BANKNIFTY", "59767.00"), change: "+0.18%" },
    { title: "FIN NIFTY", symbol: "FINNIFTY", value: getIndexLivePrice("FINNIFTY", "27820.00"), change: "-0.08%" },
    { title: "SENSEX", symbol: "SENSEX", value: getIndexLivePrice("SENSEX", "86287.00"), change: "+0.11%" },
    { title: "BANKEX", symbol: "BANKEX", value: getIndexLivePrice("BANKEX", "-"), change: "" },
    { title: "SBIN", symbol: "SBIN", value: getIndexLivePrice("SBIN", "962.40"), change: "+0.05%" },
  ];




  const getFavoritesData = async () => {
    try {
      const res = await ApiService.getFavorites(userid);
      setFavoriteusddata(res.data.data);
    } catch (err) {
      console.log(err)
    }
  }


  useEffect(() => {
    getFavoritesData();
  }, []);

  const fetchuserdetails = async () => {
    try {

      const res = await ApiService.getUserDetail(userid);
      setUserdetails(res.data.data[0]);
    } catch (err) {
      console.log(err)
    }
  }
  const handleDepositSubmit = async () => {
    if (!depositAmount || Number(depositAmount) <= 0) {
      toast.error("Enter valid deposit amount");
      return;
    }

    if (!transactionId) {
      toast.error("Enter transaction ID");
      return;
    }

    if (!receiptBase64) {
      toast.error("Upload payment receipt");
      return;
    }

    const payload = {
      userid: userid,
      Balance: Number(depositAmount),
      type: 1, // 1 = deposit
      transactionId: transactionId,
      ScreenShot: receiptBase64
    };


    try {
      if (currency === "1") {
        await ApiService.withdrawalDepositInr(payload);
      } else {
        await ApiService.withdrawalDeposit(payload);
      }
      toast.success("Deposit request submitted");
    } catch (err) {
      console.error(err);
      toast.error("Deposit failed");
    } finally {
      setDepositModal(false);
      setDepositAmount("");
      setTransactionId("");
      setReceiptBase64("");

      await fetchuserdetails();
    }

  };

  const resetDepositForm = () => {
    setDepositAmount("");
    setTransactionId("");
    setReceiptBase64("");
  };

  const closeDepositModal = () => {
    setDepositModal(false);
    resetDepositForm();
  };


  const handleReceiptUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      setReceiptBase64(reader.result); // base64
    };
    reader.readAsDataURL(file);
  };


  const fetchmarketlist = async () => {
    const payload = {
      type: typeToFetch,
    }
    try {
      const res = await ApiService.getMarketList(payload);
      setMarketAnalysis(res.data.data);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    fetchmarketlist();
  }, [typeToFetch,]);





  useEffect(() => {
    if (!sliderRef.current) return;

    const slider = sliderRef.current;
    let index = 0;

    const interval = setInterval(() => {
      index = (index + 1) % banners.length;

      slider.style.transform = `translateX(-${index * 100}%)`;

      setActiveIndex(index); // 🔥 dot yahin change ho raha
    }, 2000);

    return () => clearInterval(interval);
  }, []);


  useEffect(() => {
    if (currency === "1") {
      setTypeToFetch("volume"); // INR default
    } else if (currency === "0") {
      setTypeToFetch("Top"); // USD default
    }
  }, [currency]);



  const handleRemoveFavorite = async (item) => {

    try {
      const payload = {
        userid: userid,
        symbolname: item.symbol
      }
      const res = await ApiService.removeFavoriteUsd(payload);
      toast.success(res?.data?.message || "Favorite removed successfully");
      getFavoritesData();
    } catch (err) {
      console.log(err);
    }
  }




  return (
    <div className="container-fluid">
      {/* Main Card / Shell */}
      <div className="card shadow-lg border-0 rounded-4 overflow-hidden">


        <div className="card-body p-0 m-0">
          <div className="dashboard-trading-container">
            {/* TOP PANE: CHART */}
            <div className="dashboard-chart-pane">

              <div className="dashboard-chart-wrapper">
                <TradingViewChart
                  symbol={selectedSymbol}
                  price={livePrices[selectedToken] || getIndexLivePrice(selectedSymbol, null)}
                />
              </div>
            </div>

            {/* BOTTOM PANE: POSITIONS */}
            <div className="dashboard-positions-pane">
              <div className="positions-tabs-header">
                <div
                  className={`positions-tab ${posTab === "positions" ? "active" : ""}`}
                  onClick={() => setPosTab("positions")}
                >
                  Positions ({dashboardPositions.filter(p => p.positionstatus !== "CLOSED").length})
                </div>
                <div
                  className={`positions-tab ${posTab === "holdings" ? "active" : ""}`}
                  onClick={() => setPosTab("holdings")}
                >
                  Holdings ({dashboardHoldings.length})
                </div>
                <div
                  className={`positions-tab ${posTab === "pending" ? "active" : ""}`}
                  onClick={() => setPosTab("pending")}
                >
                  Pending (0)
                </div>
                {/* <div
                  className={`positions-tab ${posTab === "history" ? "active" : ""}`}
                  onClick={() => setPosTab("history")}
                >
                  History 24H (0)
                </div> */}
              </div>

              <div className="positions-table-container">
                <table className="positions-table">
                  <thead>
                    <tr>
                      <th>SYMBOL</th>
                      <th>TYPE</th>
                      <th>LOT</th>
                      <th>PRICE</th>
                      <th>LTP</th>
                      <th>PROFIT</th>
                    </tr>
                  </thead>
                  <tbody>
                    {posTab === "positions" && (
                      dashboardPositions.length === 0 ? (
                        <tr>
                          <td colSpan="6">
                            <div className="empty-positions">No Positions(s) Found</div>
                          </td>
                        </tr>
                      ) : (
                        dashboardPositions.map((p, idx) => {
                          const calculatedPnl = calculatePNL(p);
                          return (
                            <tr key={idx}>
                              <td className="fw-bold">{p.symbol}</td>
                              <td className={p.buy_type?.toUpperCase() === "BUY" ? "pos-buy" : "pos-sell"}>
                                {p.buy_type?.toUpperCase()}
                              </td>
                              <td>{p.buy_lot || p.lotsize}</td>
                              <td>{p.buy_price}</td>
                              <td>{livePrices[p.token] || p.liveprice || "--"}</td>
                              <td className={calculatedPnl >= 0 ? "pos-profit" : "pos-loss"}>
                                {calculatedPnl.toFixed(2)}
                              </td>
                            </tr>
                          );
                        })
                      )
                    )}

                    {posTab === "holdings" && (
                      dashboardHoldings.length === 0 ? (
                        <tr>
                          <td colSpan="6">
                            <div className="empty-positions">No Holding(s) Found</div>
                          </td>
                        </tr>
                      ) : (
                        dashboardHoldings.map((p, idx) => {
                          const calculatedPnl = calculatePNL(p);
                          return (
                            <tr key={idx}>
                              <td className="fw-bold">{p.symbol}</td>
                              <td className={p.buy_type?.toUpperCase() === "BUY" ? "pos-buy" : "pos-sell"}>
                                {p.buy_type?.toUpperCase()}
                              </td>
                              <td>{p.lotsize || p.buy_lot}</td>
                              <td>{p.buy_price}</td>
                              <td>{livePrices[p.token] || p.liveprice || "--"}</td>
                              <td className={calculatedPnl >= 0 ? "pos-profit" : "pos-loss"}>
                                {calculatedPnl.toFixed(2)}
                              </td>
                            </tr>
                          );
                        })
                      )
                    )}

                    {(posTab === "pending" || posTab === "history") && (
                      <tr>
                        <td colSpan="6">
                          <div className="empty-positions">No data found</div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {depositModal && (
        <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">

              {/* HEADER */}
              <div className="modal-header">
                <h5 className="modal-title">Add Funds</h5>
                <button className="btn-close" onClick={() => closeDepositModal()} />
              </div>

              {/* BODY */}
              <div className="modal-body">

                <div className="D-flex justify-content-between">
                  <div>
                    {/* AMOUNT INPUT */}
                    <input
                      type="number"
                      className="form-control mb-3"
                      placeholder="Enter deposit amount"
                      value={depositAmount}
                      onChange={(e) => setDepositAmount(e.target.value)}
                    />
                  </div>

                  <div>
                    {/* USD → INR PREVIEW */}
                    {currency !== "1" && depositAmount && (
                      <div className="text-end mb-2">
                        <small className="text-muted">
                          ≈ ₹{convertedInr} INR
                        </small>
                      </div>
                    )}

                  </div>
                </div>



                {/* QUICK AMOUNT BUTTONS (⬇️ QR KE NICHE) */}
                <div className="d-flex justify-content-center gap-2 mb-3">
                  {depositQuickAmounts.map((amt) => (
                    <button
                      key={amt}
                      className="btn btn-outline-secondary btn-sm"
                      onClick={() =>
                        setDepositAmount(prev =>
                          (Number(prev || 0) + amt).toString()
                        )
                      }
                    >
                      +{currency === "1" ? "₹" : "$"}{amt}
                    </button>
                  ))}
                </div>





                {currency === "1" && (
                  <div>
                    <div className="text-center mb-3">
                      <img
                        src="/qr-code.png"   // backend/static QR
                        alt="QR Code"
                        style={{ width: 180 }}
                      />
                      <small className="text-muted d-block mt-1">
                        Scan & Pay
                      </small>
                    </div>


                    <div className="mb-3">
                      <label className="form-label">Upload Payment Receipt</label>
                      <input
                        type="file"
                        className="form-control"
                        accept="image/*"
                        onChange={handleReceiptUpload}
                      />
                    </div>


                    <input
                      type="text"
                      className="form-control mb-3"
                      placeholder="Enter Transaction ID"
                      value={transactionId}
                      onChange={(e) => setTransactionId(e.target.value)}
                    />


                    <button
                      className="btn btn-success w-100"
                      onClick={handleDepositSubmit}
                    >
                      Submit Deposit
                    </button>
                  </div>
                )}



                {currency === "0" && (
                  <div>
                    <h6 className="mb-3">Select Payment Method</h6>

                    {/* Payment method cards */}
                    <div className="row g-3 mb-3">
                      {/* Crypto Transfer */}
                      <div className="col-6">
                        <div
                          className={`payment-card ${paymentMethod === "crypto" ? "active" : ""
                            }`}
                          onClick={() => setPaymentMethod("crypto")}
                        >
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <h6 className="mb-1">Crypto Transfer</h6>
                              <small className="text-muted">Online payment</small>
                            </div>

                            {paymentMethod === "crypto" && (
                              <span className="check-badge">✓</span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* INR to USDT */}
                      <div className="col-6">
                        <div
                          className={`payment-card ${paymentMethod === "inr_usdt" ? "active" : ""
                            }`}
                          onClick={() => setPaymentMethod("inr_usdt")}
                        >
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <h6 className="mb-1">INR → USDT</h6>
                              <small className="text-muted">Manual payment</small>
                            </div>

                            {paymentMethod === "inr_usdt" && (
                              <span className="check-badge">✓</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Selected method info */}
                    {paymentMethod && (
                      <div className="alert alert-success py-2 mb-3">
                        <strong>Selected Method:</strong>{" "}
                        {paymentMethod === "crypto"
                          ? "Crypto Transfer"
                          : "INR → USDT Manual Payment"}
                      </div>
                    )}

                    {/* Crypto payment form */}
                    {paymentMethod === "crypto" && (
                      <div>
                        <h6 className="mb-2 text-success">Crypto Transfer Payment</h6>

                        <div className="text-center mb-3">
                          <img src="/qr-code.png" alt="QR" style={{ width: 180 }} />
                          <small className="text-muted d-block mt-1">Scan & Pay</small>
                        </div>

                        <div className="mb-3">
                          <label className="form-label">Upload Payment Receipt</label>
                          <input
                            type="file"
                            className="form-control"
                            accept="image/*"
                            onChange={handleReceiptUpload}
                          />
                        </div>

                        <input
                          type="text"
                          className="form-control mb-3"
                          placeholder="Enter Transaction ID"
                          value={transactionId}
                          onChange={(e) => setTransactionId(e.target.value)}
                        />

                        <button
                          className="btn btn-success w-100"
                          onClick={handleDepositSubmit}
                        >
                          Submit Deposit
                        </button>
                      </div>
                    )}

                    {/* INR → USDT payment form */}
                    {paymentMethod === "inr_usdt" && (
                      <div>
                        <h6 className="mb-2 text-success">INR → USDT Manual Payment</h6>

                        <div className="text-center mb-3">
                          <img src="/qr-code.png" alt="QR" style={{ width: 180 }} />
                          <small className="text-muted d-block mt-1">Scan & Pay</small>
                        </div>

                        <div className="mb-3">
                          <label className="form-label">Upload Payment Receipt</label>
                          <input
                            type="file"
                            className="form-control"
                            accept="image/*"
                            onChange={handleReceiptUpload}
                          />
                        </div>

                        <input
                          type="text"
                          className="form-control mb-3"
                          placeholder="Enter UTR / Transaction ID"
                          value={transactionId}
                          onChange={(e) => setTransactionId(e.target.value)}
                        />

                        <button
                          className="btn btn-success w-100"
                          onClick={handleDepositSubmit}
                        >
                          Submit Deposit
                        </button>
                      </div>
                    )}
                  </div>
                )}







              </div>

            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
